import {
  TransferDestinationTypeInput,
  TransferReviewDetails,
  TransferReviewDetailsError,
  TransferReviewDetailsErrorCode,
  TransferReviewDetailsResultTypenames,
  TransferSourceTypeInput,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import ReadyTransferReview from 'features/feeBasedInstantTransfer/components/ReadyTransferReview/ReadyTransferReview';
import useSubmitReviewedTransfer from 'features/feeBasedInstantTransfer/hooks/useSubmitReviewedTransfer';
import useTransferConfirmAnalytics from 'features/feeBasedInstantTransfer/hooks/useTransferConfirmAnalytics';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import TransferReviewDetailsQuery from 'services/graphql/queries/MoveMoney/TransferReviewDetailsQuery';
import Dialog from 'shared/components/vxl/Dialog/Dialog';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

interface AliasedTransferReviewDetailsError extends TransferReviewDetailsError {
  errorTitle: string;
}

function TransferReviewScreen() {
  const { t } = useTranslation('feeBasedInstantTransfer');
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();
  const { defaultTitle, defaultMessage, retryButtonTitle, doneButtonTitle } =
    useDefaultErrorStrings();
  const { sourceAccountId, destinationId, destinationType, amount, currency } =
    useLocalSearchParams<{
      sourceAccountId?: string;
      destinationId?: string;
      destinationType?: TransferDestinationTypeInput;
      amount?: string;
      currency?: string;
    }>();
  const input =
    sourceAccountId && destinationId && destinationType && amount && currency
      ? {
          source: {
            id: sourceAccountId,
            type: TransferSourceTypeInput.Account,
          },
          destination: { id: destinationId, type: destinationType },
          amount,
          currency,
        }
      : null;

  const [reviewResult, reexecuteQuery] = useQuery<{
    transferReviewDetails:
      TransferReviewDetails | AliasedTransferReviewDetailsError | null;
  }>({
    query: TransferReviewDetailsQuery,
    variables: { input },
    requestPolicy: 'network-only',
    pause: !input,
  });
  const { data, fetching } = reviewResult;
  const review = data?.transferReviewDetails;
  const details =
    review?.__typename ===
      TransferReviewDetailsResultTypenames.TransferReviewDetails &&
    review.sections.some((section) => section.items.length > 0)
      ? review
      : undefined;
  const reviewError =
    review?.__typename ===
    TransferReviewDetailsResultTypenames.TransferReviewDetailsError
      ? review
      : undefined;

  const { status, submit, isConfirmingLeave, confirmLeave, cancelLeave } =
    useSubmitReviewedTransfer(input, details);
  const isOutcomeUnknown = status === 'outcomeUnknown';
  const { errorButtonAnalytics, logRequestSubmitted, logLeaveDialogChoice } =
    useTransferConfirmAnalytics({
      input,
      reviewResult,
      review,
      details,
      reviewError,
      isOutcomeUnknown,
      isConfirmingLeave,
    });

  const canRetryReview = reviewError
    ? reviewError.errorCode === TransferReviewDetailsErrorCode.Unavailable
    : input !== null;
  const canRetry = isOutcomeUnknown || canRetryReview;
  const errorCopy = !input
    ? {
        title: t('review.invalidInputTitle'),
        message: t('review.invalidInputMessage'),
      }
    : {
        title: reviewError?.errorTitle ?? defaultTitle,
        message: reviewError?.message ?? defaultMessage,
      };

  const handleRetry = () => {
    if (isOutcomeUnknown) {
      void submit();
    } else {
      reexecuteQuery({ requestPolicy: 'network-only' });
    }
  };

  const handleSubmit = async () => {
    logRequestSubmitted();
    await submit();
  };

  const handleStay = () => {
    logLeaveDialogChoice('stay');
    cancelLeave();
  };

  const handleLeave = () => {
    logLeaveDialogChoice('leave');
    confirmLeave();
  };

  const handleDone = () => {
    if (isOutcomeUnknown) {
      router.dismissTo('/');
    } else if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/move-money/transfer/instant-transfer');
    }
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={isOutcomeUnknown ? handleDone : undefined}
            />
          ),
        }}
      />
      <View style={styles.content}>
        {details ? (
          <ReadyTransferReview
            review={details}
            isSubmitting={status === 'submitting'}
            onSubmit={handleSubmit}
          />
        ) : null}
        <ScreenTransitionOverlay
          isLoading={fetching && !details}
          LoadingComponent={
            <LoadingSpinner accessibilityLabel={t('review.loading')} />
          }
          ErrorComponent={
            details && !isOutcomeUnknown ? null : (
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={errorCopy.title}
                messageBody={errorCopy.message}
                primaryButton={{
                  title: canRetry ? retryButtonTitle : doneButtonTitle,
                  onPress: canRetry ? handleRetry : handleDone,
                  analytics: errorButtonAnalytics(
                    canRetry ? 'retry' : 'done',
                    'primary',
                  ),
                }}
                secondaryButton={
                  canRetry
                    ? {
                        title: doneButtonTitle,
                        onPress: handleDone,
                        analytics: errorButtonAnalytics('done', 'secondary'),
                      }
                    : undefined
                }
                insetBottomSafeArea={false}
              />
            )
          }
        />
      </View>
      <Dialog
        isIOSCaptiveDialog
        isDialogVisible={isConfirmingLeave}
        setModalVisible={cancelLeave}
        titleText={t('review.leaveTitle')}
        messageText={t('review.leaveMessage')}
        dialogButtonProps={[
          {
            label: t('review.stay'),
            onClick: handleStay,
          },
          {
            label: t('review.leave'),
            onClick: handleLeave,
          },
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.color.background.primary,
  },
  content: {
    flex: 1,
  },
});

export default TransferReviewScreen;
